import { updateAppAlert } from '../../../../common/modules/appAlert/actions'
import { getErrorMessages } from '../../../../common/utils/error'
import { errorLogger } from '../../../../common/utils/errorLogger'
import { AppDispatch, RootState } from '../../../../store'
import { AxiosError, AxiosResponse } from 'axios'
import {
  createFTRAssessmentSuccess,
  deleteWorkloadSuccess,
  RequestFailureMessage,
  updatedFTRAssessmentSuccess,
} from '../../../../common/utils/messagesContants'
import { isEmpty } from 'lodash'
import { LoadingTypes, startLoading, stopLoading } from '@labrav/common'
import emptyFtrAssesment from '../../../../../src/mocks/cloudfaas/emptyFTRAssessment.json'
import { camelize, snakeize } from 'casing'
import {
  CreateFTRAssessmentRequest,
  FTRAssessmentFormDetails,
  ScopeItem,
} from './reducer'
import {
  createFTRAssessment,
  deleteWorkload,
  fetchAssessmentById,
  updateFTRAssessment,
} from '../../../api/cloudfaas/ftr'
import { awsRegionsList } from '../../../../common/utils/Cloudfaas/wafr/AssessformDataSelect'

export enum FTRAssessmentForm {
  SET_FTR_CREATE_ASSESSMENT_FORM = 'SET_FTR_CREATE_ASSESSMENT_FORM',
  SET_FTR_QUESTIONS = 'SET_FTR_QUESTIONS',
  SET_FTR_QUESTION_ID = 'SET_FTR_QUESTION_ID',
  SET_FTR_QUESTION_REF_NO = 'SET_FTR_QUESTION_REF_NO',
  SET_DELAY = 'SET_DELAY',
  REMOVE_DELAY = 'REMOVE_DELAY',
  SET_STATUS = 'SET_STATUS',
}

const PILLAR_IDS = [
  'operationalExcellence',
  'security',
  'reliability',
  'performance',
  'costOptimization',
  'sustainability',
]

export const createNewFTRAssessment =
  (
    partnerId: string,
    assessmentData: CreateFTRAssessmentRequest,
    callBackFTRWorkloadDetails?: (assessmentId: string) => void
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const snakeCasePayload = snakeize(assessmentData)

    await dispatch(startLoading(LoadingTypes.CREATE_FTR_ASSESSMENT))
    try {
      const response = await createFTRAssessment(partnerId, snakeCasePayload)

      if (response?.data) {
        const assessmentsData = camelize(response.data)
        const assessmentId = assessmentsData?.assessmentId
        await dispatch(
          invokeWorkloadCallback({
            assessmentId,
            callBackFTRWorkloadDetails,
          })
        )
      }

      dispatch(
        updateAppAlert({
          message: createFTRAssessmentSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.CREATE_FTR_ASSESSMENT))
    }
  }

export const updateFTRAssessmentData =
  (
    partnerId: string,
    assessmentId: string,
    assessmentData: CreateFTRAssessmentRequest,
    callBackFTRWorkloadDetails?: (assessmentId: string) => void
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.CREATE_FTR_ASSESSMENT))
    try {
      const snakeCasePayload = snakeize(assessmentData)

      const response = await updateFTRAssessment(
        partnerId,
        assessmentId,
        snakeCasePayload
      )

      if (!isEmpty(response?.data)) {
        const assessmentsData = camelize(response?.data)
        const assessmentId = assessmentsData?.assessmentId
        await dispatch(
          invokeWorkloadCallback({
            assessmentId,
            callBackFTRWorkloadDetails,
          })
        )
      }

      dispatch(
        updateAppAlert({
          message: updatedFTRAssessmentSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.CREATE_FTR_ASSESSMENT))
    }
  }

interface invokeWorkloadCallbackProps {
  assessmentId?: string
  callBackFTRWorkloadDetails?: (assessmentId: string) => void
}

export const invokeWorkloadCallback =
  ({ assessmentId, callBackFTRWorkloadDetails }: invokeWorkloadCallbackProps) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (assessmentId && callBackFTRWorkloadDetails) {
      callBackFTRWorkloadDetails(assessmentId)
    }
  }

export const initializeFTRAssessmentForm =
  ({ partnerId, assessmentId }: { partnerId: string; assessmentId: string }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(startLoading(LoadingTypes.SET_ASSESSMENT_FORM))
      if (isEmpty(assessmentId)) {
        const initialFormData = getInitialFTRAssessmentForm()
        await dispatch(setFTRAssessmentFormData(initialFormData))
      } else {
        await dispatch(getFTRAssessmentById(partnerId, assessmentId))
      }
    } catch (error: any) {
      const errorResponse = (error as AxiosError)
        .response as AxiosResponse<ErrorResponse>
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.SET_ASSESSMENT_FORM))
    }
  }

export const getInitialFTRAssessmentForm = () => {
  return emptyFtrAssesment
}

export const setFTRAssessmentFormData = (
  ftrAssessmentFormData: FTRAssessmentFormDetails
) => ({
  type: FTRAssessmentForm.SET_FTR_CREATE_ASSESSMENT_FORM,
  payload: { createAssessmentInfo: ftrAssessmentFormData },
})

export const setDelay = () => ({
  type: FTRAssessmentForm.SET_DELAY,
})

export const removeDelay = () => ({
  type: FTRAssessmentForm.REMOVE_DELAY,
})

export const setStatus = (status: any[]) => ({
  type: FTRAssessmentForm.SET_STATUS,
  payload: status,
})

export const deleteAssessmentById =
  (partnerId: string, assessmentId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.DELETE_WORKLOAD))
    try {
      await deleteWorkload(partnerId, assessmentId)
      dispatch(
        updateAppAlert({
          message: deleteWorkloadSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.DELETE_WORKLOAD))
    }
  }

export const getFTRAssessmentById =
  (partnerId: string, assessmentId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const response = await fetchAssessmentById(partnerId, assessmentId)
      if (response?.data) {
        const assessmentsData = camelize(response?.data)

        const mappedWorkloadAssessment = {
          id: assessmentsData.id,
          name: assessmentsData.name,
          description: assessmentsData.description,
          workloadType: assessmentsData.workloadType,
          workloadName: assessmentsData.workloadName,
          assessmentType: assessmentsData.assessmentType,
          status: assessmentsData.status,
          scope: (assessmentsData?.scope as ScopeItem[])?.map(item => ({
            accountId: item.accountId || '',
            awsRegions: item.awsRegions?.map(regionCode => {
              const match = awsRegionsList.find(r => r.region === regionCode)
              return match || { region: '', regionName: '' }
            }) || [{ region: '', regionName: '' }],
          })),
          progressBarPercentage: assessmentsData.progressBarPercentage,
        }
        await dispatch(setFTRAssessmentFormData(mappedWorkloadAssessment))
      }
      dispatch(
        updateAppAlert({
          message: '',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }
